import { X } from 'lucide-react';
import { Button } from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';

/**
 * 활성 필터 칩 1개. Applied 상태의 투영 — 라벨 + (제거 가능 시) x.
 * `onRemove` 유무로 제거 가부를 표현한다: 중립 상태(전체 옵션·default)로 폴백 가능한 필드만 제공하고,
 * required·무중립 필드는 생략해 x 를 숨긴다(끄기 차단). 제거의 의미·중립값은 호출처(feature)가 소유.
 */
export interface FilterChip {
  /** 종류 식별 key(값 충돌 회피용 — 값이 아니라 필드 종류). */
  key: string;
  label: string;
  /** 제거(x) 핸들러 — 없으면 x 미표시(required·무중립). */
  onRemove?: () => void;
}

export interface FilterChipsProps {
  chips: FilterChip[];
  /** 목록 aria-label(접근성). */
  label?: string;
  className?: string;
}

/**
 * 활성 필터 칩 행 — **전 breakpoint 노출**(필터 셸 규칙). 도메인 무지: 칩 데이터·제거 정책은 호출처가 주입.
 * 칩이 0개면 렌더하지 않는다(트리거 행만 노출). 스타일/멀티 표기 세부는 후속 정책.
 */
export function FilterChips({ chips, label, className }: FilterChipsProps) {
  if (chips.length === 0) return null;
  return (
    <ul
      aria-label={label ?? UI_TEXT.FILTER_ACTIVE}
      className={className ?? 'flex flex-wrap gap-1.5'}
    >
      {chips.map((chip) => (
        <li
          key={chip.key}
          // 말줄임: 자르는 주체는 라벨 `span`(`truncate`) — flex 컨테이너인 li 엔 text-overflow 가 안 먹는다.
          // li·span 의 `min-w-0` 은 flex item 기본 min-width:auto(=콘텐츠 폭, max-width 보다 우선) 해제용.
          className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-background px-2.5 py-1 text-xs text-muted-foreground shadow-sm dark:bg-gray-800"
        >
          <span className="min-w-0 truncate">{chip.label}</span>
          {chip.onRemove && (
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={chip.onRemove}
              aria-label={UI_TEXT.FILTER_CHIP_REMOVE(chip.label)}
              className="-mr-0.5 size-auto shrink-0 rounded-full p-0.5 text-muted-foreground/70 hover:bg-foreground/10 hover:text-foreground"
            >
              <X className="size-3" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
